<template>
    <div>
        <card-header title="New Wall Post" icon="fa-plus"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-align-left" title="Message"/>
                <container-list-item padded>
                    <b-field>
                        <b-input v-model="body" type="textarea" rows="4" placeholder="Share a moment from the trip..."/>
                    </b-field>
                </container-list-item>
            </card-list>

            <card-list>
                <subheader-list-item icon="fa-images" title="Photos & Videos" :badge="photos.length || null"/>
                <container-list-item padded>
                    <file-pond
                        name="file"
                        ref="pond"
                        label-idle="Drop or click to add photos or videos..."
                        :allow-multiple="true"
                        :allowRevert="false"
                        accepted-file-types="image/*, video/mp4, video/quicktime, video/webm"
                        :before-add-file="checkFileSize"
                        @processfilestart="uploading++"
                        @processfile="handleFilePondProcess"
                        :server="{
                            url: apiUrl,
                            timeout: 300000,
                            process: {
                                url: 'api/experience/upload',
                                method: 'POST',
                                headers: {
                                    'Authorization': token
                                },
                                withCredentials: false
                            }
                        }"
                    />
                    <div class="mt-2" v-if="photos.length">
                        <b-tag type="is-info" size="is-small" class="mr-2" v-for="(photo, i) in photos" :key="i">
                            <i class="fas fa-fw mr-1" :class="photo.file_mime && photo.file_mime.indexOf('video/') === 0 ? 'fa-video' : 'fa-image'"></i>{{ photo.file_name }}
                        </b-tag>
                    </div>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3 has-text-right">
            <b-button type="is-primary" size="is-small" @click="submit" :disabled="!valid || saving || uploading > 0">
                <i class="fas mr-2" :class="uploading > 0 ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                {{ uploading > 0 ? 'Uploading...' : 'Post to Wall' }}
            </b-button>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";

    import {client as http} from '@/http_client';
    import {mapGetters} from 'vuex';

    import vueFilePond from 'vue-filepond';
    import 'filepond/dist/filepond.min.css';
    import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
    import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
    import FilePondPluginImagePreview from 'filepond-plugin-image-preview';

    const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

    export default {
        name: "NewWallPost",
        props: ['card', 'props'],
        components: {FilePond, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        data: function() {
            return {
                apiUrl: '/',
                body: '',
                photos: [],
                saving: false,
                uploading: 0
            };
        },
        computed: {
            ...mapGetters(['token']),
            valid: function() {
                return !!this.body.trim() || this.photos.length > 0;
            }
        },
        methods: {
            submit: function() {
                if (this.saving) return;
                this.saving = true;
                this.$emit('loading', true);
                http.post('/api/experience/trips/' + this.props.tripId + '/wall', {
                    body: this.body,
                    photos: this.photos
                }, {force: true}).then(() => {
                    this.$reloadCard('trip-wall', {tripId: this.props.tripId});
                    this.$saveAlert(false);
                    this.$closeCard(this.card);
                }).catch(() => {
                    this.saving = false;
                    this.$emit('loading', false);
                });
            },
            // Runs before FilePond even queues the file — a friendly stop for
            // oversized videos instead of a failed 200MB upload attempt (the
            // server's multer limit is the same number).
            checkFileSize: function(file) {
                if (file.file.size > 200 * 1024 * 1024) {
                    this.$buefy.toast.open({type: 'is-danger', message: 'Videos can be up to 200MB.'});
                    return false;
                }
                return true;
            },
            // Fires for success AND failure; the pending counter is what keeps
            // the Post button locked until every drop has actually landed — a
            // photo must never be silently missing from the published post.
            handleFilePondProcess: function(err, file) {
                this.uploading = Math.max(0, this.uploading - 1);
                if (err) {
                    this.$buefy.toast.open({type: 'is-danger', message: 'That file didn\'t upload — please remove it and try again.'});
                    return;
                }
                try {
                    const uploaded = JSON.parse(file.serverId);
                    this.photos.push({
                        file: uploaded.file_name,
                        file_name: uploaded.file_original_name,
                        file_mime: uploaded.file_mime
                    });
                } catch (e) {
                    this.$buefy.toast.open({type: 'is-danger', message: 'That file didn\'t upload — please remove it and try again.'});
                }
            }
        },
        mounted() {
            this.$saveAlert(true, 'Are you done with this post? Unsaved changes will be lost.');
        }
    };
</script>

<style>
    .filepond--credits {
        display: none !important;
    }
</style>
